import React, {useContext} from "react";
import {AuthenticationContext, VariantContext} from "../AuthenticateView";
import {Box, Link, Paper} from "@mui/material";
import {LoginCredentials} from "../types";
import {Controller, FormProvider, useForm} from "react-hook-form";
import PasswordForgottenLink from "../../../common/PasswordForgottenLink";
import RegistrationLink from "../../../common/RegistrationLink";
import ErrorBanner from "../../../common/ErrorBanner";
import {LoadingButton} from "@mui/lab";
import {UserNameFormInput} from "./UserNameFormInput";
import {PasswordFormInput} from "./PasswordFormInput";
import {AuthenticationHandler, useAuthenticate} from "../useAuthenticate";
import {useEmailValidation} from "../../../service/useEmailValidation";
import SuccessBanner from "../../../common/SuccessBanner";
import {useLxQuery} from "@lxo-blocks/react";
import {useLxTranslations} from "@lxo-blocks/react";
import {CREDENTIALS_FORM_TRANSLATION} from "./CredentialsForm.translation.de";

interface CredentialsFormProps {
    authenticationHandler: AuthenticationHandler;
}

export default function CredentialsForm(props: CredentialsFormProps) {
    const {t, LxTrans} = useLxTranslations(CREDENTIALS_FORM_TRANSLATION);
    const authenticationContext = useContext(AuthenticationContext);
    const variant = useContext(VariantContext);
    const message = useLxQuery().get("message");
    const formMethods = useForm<LoginCredentials>({
        mode: "onSubmit",
    });

    const {authenticate, authenticationError} = useAuthenticate(props.authenticationHandler);
    const {validateEmail} = useEmailValidation();
    const {handleSubmit, formState, control} = formMethods;
    const {isSubmitting, errors} = formState;

    return (
        <>
            {/*{authenticationContext.username}*/}
            <Paper sx={{px: 8, pt: 5, pb: 2}}>
                {authenticationError && (
                    <ErrorBanner headline={t("LOGIN_FAILED")}
                                 description={authenticationError.description
                                     ? authenticationError.description
                                     : <LxTrans i18nKey={"LOGIN_FAILED_TRY_AGAIN"} components={{anchor: <Link color={"error"}/>}}/>}
                                 sx={{mb: 3}}/>)}
                {(message && message === "TOKEN_LOGIN_ERROR") && (
                    <ErrorBanner description={t("TOKEN_LOGIN_ERROR")}
                                 sx={{mb: 3}}/>)}

                {(message && message === "SET_NEW_PASSWORD_SUCCESSFULLY") && (
                    <SuccessBanner description={t("SET_NEW_PASSWORD_SUCCESSFULLY")}
                                   sx={{mb: 3}}/>)}
                <FormProvider {...formMethods}>
                    <form onSubmit={handleSubmit(authenticate)}>
                        <Controller
                            control={control}
                            name="username"
                            defaultValue={authenticationContext.username}
                            rules={{
                                required: t("EMAIL_REQUIRED").toString(),
                                validate: validateEmail
                            }}
                            render={(props: any) => <UserNameFormInput {...props} autoFocus={false}
                                                                       sx={{mb: 3}}/>}/>

                        <Controller
                            name="password"
                            control={control}
                            defaultValue=""
                            rules={{required: t("PASSWORD_REQUIRED").toString()}}
                            render={(props: any) => <PasswordFormInput {...props}
                                                                       autoFocus={!!authenticationContext.username}
                                                                       sx={{mb: 3}}/>}
                        />

                        <LoadingButton
                            sx={{py: 1}}
                            variant="contained"
                            color="primary"
                            type="submit"
                            fullWidth
                            disabled={!!errors.username || !!errors.password}
                            loadingPosition="end"
                            endIcon={<></>}
                            loading={isSubmitting}
                        >{t("SIGNIN")}
                        </LoadingButton>
                    </form>
                </FormProvider>

                <Box sx={{pt: 3, pb: 1}}>
                    {!variant && <PasswordForgottenLink/>}
                </Box>
            </Paper>

            {!variant && <RegistrationLink sx={{pt: 3, pb: 1}}/>}
        </>
    );
}
